<script lang="ts">
	import * as m from '$lib/paraglide/messages';

	type TkType = 'numeric' | 'duration' | 'scale' | 'bool';
	let current = $state<TkType>('numeric');
	let num = $state(12);
	let scale = $state(8);
	let bool = $state(true);

	const TYPES: { key: TkType; label: () => string }[] = [
		{ key: 'numeric', label: () => m.features_tk_type_numeric() },
		{ key: 'duration', label: () => m.features_tk_type_duration() },
		{ key: 'scale', label: () => m.features_tk_type_scale() },
		{ key: 'bool', label: () => m.features_tk_type_bool() }
	];
	const META: Record<TkType, { name: () => string; dot: string }> = {
		numeric: { name: () => m.features_tk_name_numeric(), dot: 'var(--cat-violet)' },
		duration: { name: () => m.features_tk_name_duration(), dot: 'var(--cat-amber)' },
		scale: { name: () => m.features_tk_name_scale(), dot: 'var(--cat-emerald)' },
		bool: { name: () => m.features_tk_name_bool(), dot: 'var(--cat-red)' }
	};
</script>

<div class="demo-body">
	<div class="seg-tabs">
		{#each TYPES as t (t.key)}
			<button data-active={current === t.key} onclick={() => (current = t.key)}>{t.label()}</button>
		{/each}
	</div>
	<div class="tk-field">
		<div class="tk-head">
			<span class="dot" style:background={META[current].dot}></span>
			<b>{META[current].name()}</b>
			<span class="ty">{current}</span>
		</div>
		<div>
			{#if current === 'numeric'}
				<div class="tk-input">
					<button class="tk-step" type="button" onclick={() => (num = Math.max(0, num - 1))}
						>−</button
					>
					<div class="tk-num">{num}<small>{m.features_tk_unit_pages()}</small></div>
					<button class="tk-step" type="button" onclick={() => (num = num + 1)}>+</button>
				</div>
			{:else if current === 'duration'}
				<div class="tk-input">
					<button class="tk-step" type="button">−</button>
					<div class="tk-dur">1<small>h</small> 30<small>m</small></div>
					<button class="tk-step" type="button">+</button>
				</div>
			{:else if current === 'scale'}
				<div class="tk-scale">
					{#each Array.from({ length: 10 }, (_, i) => i + 1) as v (v)}
						<span
							class:on={v <= scale}
							role="button"
							tabindex="0"
							aria-label={`${v} / 10`}
							onclick={() => (scale = v)}
							onkeydown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									scale = v;
								}
							}}
						></span>
					{/each}
				</div>
				<div class="tk-scale-val">{scale} / 10</div>
			{:else}
				<div class="tk-bool">
					<button data-on={bool} onclick={() => (bool = true)}>{m.features_tk_yes()}</button>
					<button data-on={!bool} onclick={() => (bool = false)}>{m.features_tk_no()}</button>
				</div>
			{/if}
		</div>
	</div>
</div>

<style>
	/* Port from docs/superpowers/specs/marketing-prototype/features.html <style> */
	.seg-tabs {
		display: inline-flex;
		gap: 2px;
		padding: 3px;
		border: 1px solid var(--v3-border);
		border-radius: 9999px;
		background: var(--v3-bg);
	}
	.seg-tabs button {
		border: none;
		background: transparent;
		cursor: pointer;
		font-family: inherit;
		font-size: 0.78rem;
		font-weight: 500;
		color: var(--v3-muted);
		padding: 6px 12px;
		border-radius: 9999px;
		transition: all 0.2s ease;
	}
	.seg-tabs button:hover {
		color: var(--v3-text);
	}
	.seg-tabs button[data-active='true'] {
		background: var(--v3-text);
		color: var(--v3-bg);
	}
	.tk-field {
		margin-top: 1.25rem;
		border: 1px solid var(--v3-border);
		border-radius: 12px;
		padding: 1.1rem 1.25rem;
		background: var(--v3-bg);
		display: flex;
		flex-direction: column;
		gap: 0.9rem;
		min-height: 150px;
	}
	.tk-head {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	.tk-head .dot {
		width: 9px;
		height: 9px;
		border-radius: 50%;
	}
	.tk-head b {
		font-size: 0.95rem;
		font-weight: 600;
	}
	.tk-head .ty {
		margin-left: auto;
		font-size: 0.7rem;
		color: var(--v3-muted);
		border: 1px solid var(--v3-border);
		border-radius: 9999px;
		padding: 3px 9px;
	}
	.tk-input {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	.tk-num {
		font-family: var(--font-mono);
		font-size: 2.2rem;
		font-weight: 700;
		letter-spacing: -0.03em;
	}
	.tk-num small {
		font-size: 0.9rem;
		color: var(--v3-muted);
		font-weight: 400;
		margin-left: 4px;
	}
	.tk-step {
		width: 34px;
		height: 34px;
		border-radius: 9px;
		border: 1px solid var(--v3-border);
		background: var(--v3-surface);
		color: var(--v3-text);
		font-size: 18px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.tk-step:hover {
		border-color: var(--v3-border-strong);
	}
	.tk-scale {
		display: flex;
		gap: 5px;
	}
	.tk-scale span {
		flex: 1;
		height: 30px;
		border-radius: 6px;
		background: var(--v3-faint);
		cursor: pointer;
		transition: background 0.15s ease;
	}
	.tk-scale span.on {
		background: var(--cat-emerald);
	}
	.tk-scale-val {
		font-family: var(--font-mono);
		font-size: 0.85rem;
		color: var(--v3-muted);
		margin-top: 2px;
	}
	.tk-bool {
		display: flex;
		gap: 8px;
	}
	.tk-bool button {
		flex: 1;
		padding: 12px;
		border-radius: 10px;
		border: 1px solid var(--v3-border);
		background: var(--v3-bg);
		color: var(--v3-muted);
		cursor: pointer;
		font-family: inherit;
		font-size: 0.9rem;
		font-weight: 500;
		transition: all 0.15s ease;
	}
	.tk-bool button[data-on='true'] {
		background: var(--v3-text);
		color: var(--v3-bg);
		border-color: var(--v3-text);
	}
	.tk-dur {
		display: flex;
		align-items: baseline;
		gap: 4px;
		font-family: var(--font-mono);
		font-size: 2rem;
		font-weight: 700;
		letter-spacing: -0.03em;
	}
	.tk-dur small {
		font-size: 0.85rem;
		color: var(--v3-muted);
		font-weight: 400;
	}
</style>
